import React, { memo, useContext, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext } from 'src/contexts/modal-context';
import { editableFields } from '../constants';
import { FormBuilder } from '@components/form';
import { translate } from '@components/i18n';
import { CostResponse } from '@models/cost';
import { createCost, removeCost, updateCost } from '@redux/slices/cost';
import moment from 'moment';
import { orderItemsSelector } from '@redux/slices/order/select-items';

interface EditModalProps {
    data?: CostResponse;
}

export const EditModal: React.FC<EditModalProps> = memo(({ data }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<CostResponse>();
    const [editState, setEditState] = useState<CostResponse>({ ...data });
    const {moedas} = useSelector(orderItemsSelector)
    const modalContext = useContext(ModalContext);

    const onOk = (values): void => {
        if (!data) addNew(values);
        else handleUpdate(values);
    };

    const handleUpdate = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.updatingItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            updateCost({
                data: {
                    id: data.id,
                    ...values,
                    dataInicial: values.dataInicial
                        ? moment(values.dataInicial).format('YYYY-MM-DD')
                        : null,
                    dataFinal: values.dataFinal
                        ? moment(values.dataFinal).format('YYYY-MM-DD')
                        : null,
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                },
            }),
        );
    };

    const addNew = (values): void => {
        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.registeringItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            createCost({
                data: {
                    ...values,
                    dataInicial: values.dataInicial
                        ? moment(values.dataInicial).format('YYYY-MM-DD')
                        : null,
                    dataFinal: values.dataFinal
                        ? moment(values.dataFinal).format('YYYY-MM-DD')
                        : null,
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                },
            }),
        );
    };

    const onChangeValues = (
        changedValues: Partial<CostResponse>,
        allValues: CostResponse,
    ): void => {
        setEditState({
            ...allValues,
            ...changedValues,
        });
    };

    const handleDeleteUser = (): void => {
        dispatch(removeCost(data.id));
        modalContext.handleClose();
    };

    return (
        <Row align="middle">
            <Col span={1} />
            <Col span={22}>
                <FormBuilder
                    onValuesChange={onChangeValues}
                    layout="vertical"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{
                        ...editState,
                        dataInicial: editState?.dataInicial
                            ? moment(editState?.dataInicial)
                            : undefined,
                        dataFinal: editState?.dataFinal
                            ? moment(editState?.dataFinal)
                            : undefined,
                    }}
                    onFinish={onOk}
                    form={form}
                    fields={editableFields({moedas})}
                    withDelete={!!data}
                    onDelete={handleDeleteUser}
                />
            </Col>
            <Col span={1} />
        </Row>
    );
});
